import { Box, PageHeader, Text } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type {
    IServiceOrganizationNotificationSettings,
    IServicesByServiceGroupForEpisodeCategory,
} from '@common/interfaces';
import { ManagerAuthorization, ServiceType } from '@common/enums';
import { Loader } from '@frontend/components/elements/common';
import { SettingsCategoryFilterButtonRow } from '@frontend/components/elements/SettingsCategoryFilterButtonRow';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import { OrganizationContractSettings } from './organizationContractSettings/OrganizationContractSettings';
import { FundingSettingAddEdit } from './serviceSettings/FundingSettingAddEdit';

export function OrganizationFunding() {
    assertHasAuthorization(ManagerAuthorization.organizationRead);

    const { organizationId } = useParams() as { organizationId: string };
    const { episodeCategory } = useParams() as { episodeCategory: EpisodeCategory };

    const [serviceOrganizationSettings, setServiceOrganizationSettings] = useState<
        IServiceOrganizationNotificationSettings[]
    >([]);
    const [serviceOrganizationSettingsByServiceGroupName, setServiceOrganizationSettingsByServiceGroupName] = useState<{
        [k: string]: IServiceOrganizationNotificationSettings[];
    }>({});
    const [serviceGroupNames, setServiceGroupNames] = useState<string[]>([]);
    const [contractuallyFundedServiceTypes, setContractuallyFundedServiceTypes] = useState<ServiceType[]>([]);
    const [episodeCategoryId, setEpisodeCategoryId] = useState<string>();
    const [selectedServiceGroupTab, setSelectedServiceGroupTab] = useState<{ displayText: string; id: string }>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const showServiceGroupChoice = serviceGroupNames.length > 1;

    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        setIsLoading(true);
        load(true);
    }, [episodeCategory]);

    if (isLoading) {
        return (
            <Loader
                fixedCentered={true}
                large={true}
            />
        );
    } else if (contractuallyFundedServiceTypes.length && episodeCategoryId) {
        return (
            <>
                <PageHeader
                    title={`${translateKey('contractSettings.priceAgreements')} ${translateKey(
                        `episodeCategoryEnum.${episodeCategory}`
                    )}`}
                />

                <OrganizationContractSettings
                    episodeCategoryId={episodeCategoryId}
                    organizationId={organizationId}
                    serviceTypes={contractuallyFundedServiceTypes}
                />
            </>
        );
    } else {
        return (
            <>
                <PageHeader
                    title={`${translateKey('common.funding')} ${translateKey(`episodeCategoryEnum.${episodeCategory}`)}`}
                />

                {showServiceGroupChoice && renderTabs()}

                {renderBlocks()}
            </>
        );
    }

    async function load(hasEpisodeCategoryChanged: boolean = false) {
        const sosUrl = `/api/serviceOrganizationSettings?organizationId=${organizationId}&episodeCategory=${episodeCategory}&orderByStartDate=true&orderByStartDateDirection=DESC&excludeServicesWithoutServiceGroup=true`;
        const sosResult = await abortableFetch.fetchJson(sosUrl, 'GET');
        const serviceOrganizationSettingResults = sosResult.json.items as IServiceOrganizationNotificationSettings[];
        const serviceOrganizationSettingsForCurrentOrganization = serviceOrganizationSettingResults.filter(
            (sos) => sos.service.serviceType !== ServiceType.other
        );

        if (serviceOrganizationSettingsForCurrentOrganization.length) {
            setEpisodeCategoryId(serviceOrganizationSettingsForCurrentOrganization[0].service.episodeCategoryId);
        }

        if (
            serviceOrganizationSettingsForCurrentOrganization.find(
                (serviceOrganizationSetting) => serviceOrganizationSetting.service.contractFunding
            )
        ) {
            const serviceTypesWithContractFunding = serviceOrganizationSettingsForCurrentOrganization
                .filter((serviceOrganizationSetting) => serviceOrganizationSetting.service.contractFunding)
                .map((serviceOrganizationSetting) => serviceOrganizationSetting.service.serviceType);

            const orderedFundedContractableServiceTypes = [
                ServiceType.examination,
                ServiceType.counseling,
                ServiceType.consultation,
            ];

            setContractuallyFundedServiceTypes([
                ...new Set(
                    orderedFundedContractableServiceTypes
                        .filter((serviceType) => serviceTypesWithContractFunding.includes(serviceType))
                        .concat(serviceTypesWithContractFunding)
                ),
            ]);
        } else {
            const sgmUrl = '/api/serviceGroupMemberships?groupByEpisodeCategory=true';
            const sgmResult = await abortableFetch.fetchJson(sgmUrl, 'GET');
            const serviceGroupsForCurrentEpisodeCategory = (
                sgmResult.json.items as IServicesByServiceGroupForEpisodeCategory[]
            ).find((sg) => sg.episodeCategory.name === episodeCategory)?.serviceGroups;

            const serviceOrganizationSettingsByServiceGroup = {} as {
                [k: string]: IServiceOrganizationNotificationSettings[];
            };

            const tabNames: string[] = [];

            for (const serviceOrganizationSetting of serviceOrganizationSettingsForCurrentOrganization) {
                const serviceGroup = serviceGroupsForCurrentEpisodeCategory
                    ? serviceGroupsForCurrentEpisodeCategory.find((group) =>
                          group.services?.find((service) => service.id === serviceOrganizationSetting.service.id)
                      )
                    : undefined;

                const serviceGroupName = serviceGroup ? serviceGroup.name : translateKey('common.other');

                if (!serviceOrganizationSettingsByServiceGroup[serviceGroupName]) {
                    serviceOrganizationSettingsByServiceGroup[serviceGroupName] = [];
                }

                serviceOrganizationSettingsByServiceGroup[serviceGroupName].push(serviceOrganizationSetting);
                if (!tabNames.includes(serviceGroupName)) {
                    tabNames.push(serviceGroupName);
                }
            }

            if (tabNames.includes(translateKey('common.other'))) {
                tabNames.push(tabNames.splice(tabNames.indexOf(translateKey('common.other')), 1)[0]);
            }

            if (!selectedServiceGroupTab || hasEpisodeCategoryChanged) {
                setSelectedServiceGroupTab({
                    displayText: tabNames[0],
                    id: tabNames[0],
                });
            }

            setServiceGroupNames(tabNames);
            setServiceOrganizationSettings(serviceOrganizationSettingsForCurrentOrganization);
            setServiceOrganizationSettingsByServiceGroupName(serviceOrganizationSettingsByServiceGroup);
            setContractuallyFundedServiceTypes([]);
        }

        setIsLoading(false);
    }

    function renderTabs() {
        if (!serviceGroupNames) {
            return null;
        }

        return (
            <Box mb={8}>
                <SettingsCategoryFilterButtonRow
                    collection={serviceGroupNames.map((name) => ({ displayText: name, id: name }))}
                    onClick={setSelectedServiceGroupTab}
                    selectedItem={selectedServiceGroupTab}
                />
                <Text mt={4}>{translateKey('fundingSettings.fundingSettingExplanation')}</Text>
            </Box>
        );
    }

    function renderBlocks() {
        if (!selectedServiceGroupTab) {
            return;
        }

        const serviceOrganizationSettingsList = showServiceGroupChoice
            ? serviceOrganizationSettingsByServiceGroupName[selectedServiceGroupTab.id]
            : serviceOrganizationSettings;

        return serviceOrganizationSettingsList?.map((sos) => (
            <FundingSettingAddEdit
                key={sos.id}
                onSubmit={() => load()}
                serviceOrganizationSetting={sos}
            />
        ));
    }
}
